import { ExitObjectOutputType, NodeTypeName } from "@app/types";
import React from "react";
import ReactDOM from "react-dom";
import PortView from "./PortView";
import SavePromptNodeModel from "../models/nodes/SavePromptNodeModel";
import PlaySavedPromptNodeModel from "../models/nodes/PlaySavedPromptNodeModel";
import ReportNodeModel from "../models/nodes/ReportNodeModel";
import PromptNodeModel from "../models/nodes/PromptNodeModel";
import ModuleNodeModel from "../models/ModuleNodeModel";
import CustomNodeModel from "../models/nodes/CustomNodeModel";
import DataNodeModel from "../models/nodes/DataNodeModel";
import ExitNodeModel from "../models/nodes/ExitNodeModel";
import ModuleUsedIn from './../utilities/usedIn/ModuleUsedIn';
import ObjectsUsedIn from './../utilities/usedIn/ObjectsUsedIn';
import InputNodeModel from '../models/nodes/InputNodeModel';
import UserTypeStorage from '../utilities/UserTypeStorage';
import NodeRegistry from "../utilities/NodeRegistry";
import { getPromptText } from "../utilities";

export default class NodeView extends React.Component {
    /**
	 * Normal node view, with mouse and custom event handling
	 * @module NodeView
     * @param {Object} props React props
     */
	constructor(props) {
		super(props);
		this.state = {
			showDetail: true,
			view: this.props.diagramEngine.getDiagramModel().viewRenderMode ? this.props.diagramEngine.getDiagramModel().viewRenderMode : "flow",
			usedInCount: 0,
			userType: UserTypeStorage.getUserType()
		};
		this.eventHandle = this.handleEvent.bind(this);
		this.portContainer = null;
		this.domSelf = null;
		this.updateUser = this.updateUserType.bind(this);
	}

	updateUserType() {
        this.setState({"userType": UserTypeStorage.getUserType()});
        
    }

    /**
	 * Get reference to node itself
     * @returns {jQuery}
     */
	getDomSelf() {
		if (!this.domSelf) {
			this.domSelf = $(ReactDOM.findDOMNode(this));
		}
		return this.domSelf;
	}

    /**
	 * Is the node a special node? (e.g. note, intent)
     * @returns {boolean}
     */
	isSpecialNode() {
		return this.props.node.isSpecialNode();
	}

    /**
	 * Update position cache for node ports
     * @param {null|number} newHeight If non-null, use the value directly instead of doing a new calculation
     */
	updatePortCenterOffsets(newHeight = null) {
		if (this.isSpecialNode()) {
			return;
		}
		//DEVDES2-61
		//this.props.node.getPorts()["bottom"].centerOffset = { x: 70, y: newHeight ? newHeight : this.bottomPortDom.offsetTop + 15 };
		this.props.node.getPorts()["bottom"].centerOffset = { x: 90, y: newHeight ? newHeight : this.bottomPortDom.offsetTop + 15 };
	}

	componentDidMount() {
		document.addEventListener('ev-update-userType', this.updateUser);
		if(this.props.node.type === 'module'){
			const usedInCount = this.getUsedInCount(this.props.node, window.apm.serialized);
			this.setState({usedInCount, usedInCount})
		}
		this.portContainer = this.getDomSelf().find('.generic-port-container');
		this.bottomPortDom = this.getDomSelf().find('.generic-port-container.bottom').get(0);
		this.updatePortCenterOffsets();
		
		document.addEventListener('ev-update-focus', this.eventHandle);
		document.addEventListener('ev-canvas-zoom-change', this.eventHandle);
		document.addEventListener('ev-canvas-view-change', this.eventHandle);
		document.addEventListener('ev-graph-node-ignore', this.eventHandle);
	}

	componentWillUpdate() {
		if (this.isSpecialNode()) {
			return;
		}
		this.tempStyle = {
			height: this.bottomPortDom.offsetTop + 15
		};
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		const newUsedInCount = this.getUsedInCount(this.props.node, window.apm.serialized);
		if(prevState.usedInCount !== newUsedInCount){
			this.setState({usedInCount: newUsedInCount})
		}
		if (this.isSpecialNode()) {
			return;
		}
		const newHeight = this.bottomPortDom.offsetTop + 15;
		if (newHeight !== this.tempStyle.height) {
			this.updatePortCenterOffsets(newHeight);
			window.apm.postEventToCurrentView({ target: "request-refresh-links" });
		}
		this.tempStyle = {};
	}

	componentWillUnmount() {
		document.removeEventListener('ev-update-focus', this.eventHandle);
		document.removeEventListener('ev-canvas-zoom-change', this.eventHandle);
		document.removeEventListener('ev-canvas-view-change', this.eventHandle);
		document.removeEventListener('ev-graph-node-ignore', this.eventHandle);
		document.removeEventListener('ev-update-userType', this.updateUser);
	}

    /**
	 * Handle document event
     * @param {Object} event Document CustomEvent
     */
	handleEvent(event) {
		if (event.type === "ev-update-focus") {
			// optimized, do this only for nodes in current view
			if (event.detail === this.props.diagramEngine.clientId) {
				// if node is a module, update its usedInCount state
				if(this.props.node.type === 'module'){
					const usedInCount = this.getUsedInCount(this.props.node, window.apm.serialized);
					this.setState({usedInCount, usedInCount})
				}	
				document.addEventListener('ev-canvas-zoom-change', this.eventHandle);
			} else {
				document.removeEventListener('ev-canvas-zoom-change', this.eventHandle);
			}
		} else if (event.type === "ev-canvas-zoom-change") {
			this.handleZoomChange(event);
		} else if (event.type === "ev-canvas-view-change") {
			this.handleViewChange(event);
		} else if (event.type === 'ev-graph-node-ignore') {
			if (event.detail === this.props.node.getID()) {
				this.forceUpdate();
			}
		}
	}

    /**
	 * Handle view change when zooming in / out canvas; hide detail when zoomed out
     * @param {Object} event Document CustomEvent
     */
	handleZoomChange(event) {
		if (event.detail.cliendId !== this.props.diagramEngine.clientId) {
			document.removeEventListener('ev-canvas-zoom-change', this.eventHandle);
			return;
		}
		if (event.detail.zoom < 0.6) {
			this.setState({ showDetail: false });
		} else {
			this.setState({ showDetail: true });
		}
	}

    /**
	 * Handle view change to show either data or flow view
     * @param {Object} event Document CustomEvent
     */
	handleViewChange(event) {
		this.setState({ view: event.detail });
	}

    /**
	 * Render note node
     * @returns {XML}
     */
	renderNote() {
		// Text overflow
		var desc = this.props.node.getConfig("note");
		if (!desc) {
			return (
				<div className={"basic-node note"}>
					<div className="title">
						<div className="name">
							<p><em className="greyout">Add some note...</em></p>
						</div>
					</div>
				</div>
			);
		}
		if (desc.length > 150) {
			desc = desc.substring(0, 150) + "…";
		}
		const lines = desc.split(/\r?\n/g);
		var displayLines = [];
		for (var i = 0; i < lines.length; i++) {
			displayLines.push(lines[i]);
			displayLines.push(<br key={i} />);
		}
		displayLines.pop();

		return (
			<div className={"basic-node note"}>
				<div className="title">
					<div className="name">
						<p>{displayLines}</p>
					</div>
				</div>
			</div>
		);
	}

    /**
	 * Render intent node
     * @returns {XML}
     */
	renderIntent() {
		return (
			<div
				onMouseEnter={() => {
					this.portContainer.animateCss("bounceIn").addClass("highlight");
				}}
				onMouseLeave={() => {
					this.portContainer.removeClass("highlight");
				}}
				className={"basic-node intent grey" + (this.props.node.isIgnored ? " ignored" : "")}
			>
				<div className="title">
					<div className="name">
						<div className="icon">
							<i className={"fe icon-" + this.props.node.props.iconClass} />
						</div>
						{this.props.node.name}
					</div>
				</div>
				<div className={"generic-port-container left"}>
					<PortView model={this.props.node.getPorts().left} />
				</div>
				<div className={"generic-port-container top"}>
					<PortView model={this.props.node.getPorts().top} />
				</div>
				<div className={"generic-port-container right"}>
					<PortView model={this.props.node.getPorts().right} />
				</div>
				<div className={"generic-port-container bottom"}>
					<PortView model={this.props.node.getPorts().bottom} />
				</div>
			</div>
		);
	}

	renderDescription() {
		const desc = this.getDescription();
		if (typeof desc === 'string' && desc.length > 170) {
			return desc.substring(0, 170) + "…";
		}
		return desc;
	}

	getDescription() {
		let { node } = this.props;
		if (node instanceof SavePromptNodeModel || node instanceof PlaySavedPromptNodeModel) {
			const nodeConfig = node.getConfig("promptId");
			node = nodeConfig && NodeRegistry?.buildNode(
				window.apm.getNode(nodeConfig)
			);
		}
		if (!node) {
			return null;
		}
		if (node instanceof PromptNodeModel) {
			return getPromptText({node});
		}

		if (node instanceof ExitNodeModel) {
			const outputs = node.getConfig("outputs");
			if (outputs?.length) {
				var lines = [];
				for (var i = 0; i < outputs.length; i++) {
					const output = outputs[i];
					if (output.type === ExitObjectOutputType.variable) {
						lines.push(output.varName);
					} else if (output.type === ExitObjectOutputType.value) {
						lines.push(<span key={i + "s"}><i className={"fe icon-sliders"} style={{ color: "var(--theme-text-muted)" }} /> {output.value}</span>);
					} else {
						var intName = "???";
						const intentNode = window.apm.getNode(output.intent);
						if (intentNode) {
							intName = intentNode.name;
						}
						lines.push(<span key={i + "s"}><i className={"fe icon-radio"} style={{ color: "var(--theme-text-muted)" }} /> {intName}</span>);
					}
					lines.push(<br key={i} />);
				}
				lines.pop();
				return lines
			}
		}
		if (node instanceof InputNodeModel) {
			if (!node.getConfig("description") && node.props.config.inputType) {
				const inputTypes = window.apm.knowledgeBase.getInputTypes();
				const inputType = inputTypes.find((it) => it.value === node.props.config.inputType);
				return inputType.name;
			}
		}
		return node.getConfig("description");
	}

  /**
	 * Render variable list for node input and output
     * @param {Array} variables
     * @returns {Array|*}
     */
	renderVariables(variables) {
		if (!variables || variables.length <= 0) {
			return;
		}
		var lines = [];
		for (var i = 0; i < variables.length; i++) {
			if (variables[i].varName) {
				lines.push(variables[i].varName);
			} else {
				lines.push(<span key={i + "s"}><i className={"fe icon-sliders"} style={{ color: "var(--theme-text-muted)" }} /> {variables[i].value}</span>);
			}
			lines.push(<br key={i} />);
		}
		lines.pop();
		return lines;
	}

	getUsedInCount(node, serialized){
		let usedInCount = 0;

		if(node.type === 'module' && serialized){
			const mod = serialized.components.modules.find(mod => mod.id === node.props.parentComponentID);
			if (mod) {
				const usedIn = ModuleUsedIn.getModuleUsedIn(mod, serialized);
				Object.values(usedIn).forEach(arr => {
					usedInCount += arr.length;
				})
			}
		}

		if(node.props.nodeTypeName === NodeTypeName.custom && serialized){
			const mod = serialized.components.objects.find(mod => mod.id === node.props.parentComponentID);
			if (mod) {
				const usedIn = ObjectsUsedIn.getObjectUsedIn(mod.id, serialized);
				Object.values(usedIn).forEach(arr => {
					usedInCount += arr.length;
				})
			}
		}
		
		return usedInCount;

	}

	/**
	 * Node color class override for rendering nodes in various display modes
	 */
	getColorClass(){
		if (this.props.node.props.config.disabledInFlow && !(window.apm.serialized.displayPrefs && window.apm.serialized.displayPrefs.hideDisabledElements)) {
			return "grey";
		} else {
            return this.props.node.props.colorClass || this.props.node.getDefaultColorClass();
        }
	}

	/**
	 * Returns Empty Script warning if Custom Nodes and Data Access Nodes have no scripts
     */

	getNoScriptLabel() {

		//Only triggers for Developer and Quality user types
		const checkUserType = this.state.userType !== "designer" && this.state.userType !== "client";

		// Customs and Data Access Nodes store scripts in differently named properties
		const checkCustom = this.props.node instanceof CustomNodeModel && !this.props.node.getConfig("script");
		const checkDataAccess = this.props.node instanceof DataNodeModel && !this.props.node.getConfig("customScript");

		if(checkUserType && (checkCustom || checkDataAccess)) {
			return <label className="node-warning"><i className="fe icon-alert-circle" />empty script</label>
		}
	}

    /**
	 * React render
     * @returns {XML}
     */
	render() {
		const colorClass = (this.props.node.props.colorClass) ? " " + this.getColorClass() : "";
		const hasError = this.props.node.hasError();
		/*DEVDES2-61*/
		const nameChunks = [];
		const bits = this.props.node.name.split(/([ _])/g);
		for (var i = 0; i < bits.length; i++) {
			if (i < bits.length - 1) {
				nameChunks.push(bits[i]);
				nameChunks.push(<wbr key={i} />);
			} else {
				nameChunks.push(bits[i]);
			}
		}
		/*--*/

		if (this.props.node.props.colorClass === "note") {
			return this.renderNote();
		}
		if (this.props.node.props.colorClass === "intent") {
			return this.renderIntent();
		}

		if (this.props.node.hasError() && (this.state.selected || this.props.node.isSelected())) {
			window.apm.postEventToCurrentView({
				target: "request-element-error-message",
				type: "show",
				context: this.props.node
			});
		} else {
			window.apm.postEventToCurrentView({ target: "request-element-error-message", context: this.props.node });
		}

		return (
			<div
				onMouseEnter={event => {
					//this.getDomSelf().find('.node-content').animateCss("zoomIn").addClass("highlight");
					this.portContainer.animateCss("bounceIn").addClass("highlight");
				}}
				onMouseLeave={event => {
					this.portContainer.removeClass("highlight");
				}}
				className={"basic-node" + colorClass + (hasError ? " error" : "") + (this.props.node.isIgnored ? " ignored" : "")}
			>
				<div className={
					"title"
					+ (this.state.showDetail ? "" : " large")
					+ (hasError ? " error" : "")
				}>
					<div className="icon">
						{!hasError && <i className={"fe icon-" + this.props.node.props.iconClass} />}
						{hasError && <i className={"fe icon-alert-triangle"} />}
					</div>
					<div className="name">{nameChunks}</div>
					<div style={{ display: "inline-flex", gap: '.25em' }}>					
						{/* TODO: usedInCount will always be zero until getUsedIn method is performant */}
						{this.state.usedInCount > 1 && (<i className="fe icon-recycle"/>)}
						{this.props.node instanceof ModuleNodeModel && (<i className={"fe icon-external-link"}/>)}

					</div>
						{this.props.node instanceof ReportNodeModel &&this.props.node.props.config.oncePerCall&& (
						<div>{"$1x"}</div>
					)}
				</div>
				{this.state.showDetail && (
					<div className="node-content">
						{this.state.view === "flow" && (
							<p>{this.renderDescription()}</p>
						)}
						{this.state.view === "data" && (
							<table>
								<tbody>
									<tr>
										<td className={"in"}>&#9656; IN</td>
										<td className={"in-var"}>
											{this.renderVariables(this.props.node.getConfig("inputs"))}
										</td>
									</tr>
									<tr>
										<td className={"out"}>OUT &#9656;</td>
										<td className={"out-var"}>
											{this.renderVariables(this.props.node.getConfig("outputs"))}
										</td>
									</tr>
								</tbody>
							</table>
						)}
						{this.getNoScriptLabel()}
					</div>
				)}
				<div className={"generic-port-container left" + (hasError ? " error" : "")}>
					<PortView model={this.props.node.getPorts().left} />
				</div>
				<div className={"generic-port-container top" + (hasError ? " error" : "")}>
					<PortView model={this.props.node.getPorts().top} />
				</div>
				<div className={"generic-port-container right" + (hasError ? " error" : "")}>
					<PortView model={this.props.node.getPorts().right} />
				</div>
				<div className={"generic-port-container bottom" + (hasError ? " error" : "")}>
					<PortView model={this.props.node.getPorts().bottom} />
				</div>
			</div>
		);
	}
}
